import { useState } from 'react';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import { Info, Loader2, MoveLeft } from 'lucide-react';

import Icon from 'components/icon/Icon';
import { Button } from 'components/ui/button';
import { InititaQRSession, ScanLinkChannel } from 'services/face-scan/type';
import { sendScanLink } from 'services/face-scan/api';
import SelectedMediaInfo from './SelectedMediaInfo';
import useCountdownTimer from 'hooks/useCountdownTimer';
import { cn } from 'lib/utils';
import Loader from 'components/Loader';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';

interface ScanWithMobileProps {
  qrData: InititaQRSession;
  onBack: () => void;
  onGenerate: () => Promise<void>;
  isLoading: boolean;
  connectWebSocket: (data: { sessionId: string }) => void;
}

export default function ScanWithMobile({
  qrData,
  onBack,
  onGenerate,
  isLoading,
  connectWebSocket,
}: ScanWithMobileProps) {
  const { t } = useTranslation();

  const [selectedMode, setSelectedMode] = useState<ScanLinkChannel>();
  const [isSending, setIsSending] = useState<ScanLinkChannel>();

  const { getRemainingTime, restartTimer } = useCountdownTimer(10);
  const { minutes, seconds } = getRemainingTime();

  const onSendLink = async (channel: ScanLinkChannel) => {
    try {
      setIsSending(channel);
      const res = await sendScanLink(channel);
      setSelectedMode(channel);
      connectWebSocket({ sessionId: res.session_id });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(undefined);
    }
  };

  if (selectedMode) {
    return (
      <SelectedMediaInfo
        mode={selectedMode}
        onBack={() => {
          setSelectedMode(undefined);
          onBack();
        }}
      />
    );
  }

  const onRegenerate = async () => {
    await onGenerate();
    restartTimer();
  };

  if (isLoading) {
    return <Loader className='h-[60vh]' />;
  }

  return (
    <div className='max-w-5xl mx-auto min-h-[calc(100vh-10rem)] py-8 flex flex-col items-center justify-center relative space-y-8'>
      <div className='absolute left-0 top-8'>
        <Button variant='ghost' className='rounded-md' onClick={onBack}>
          <MoveLeft className='stroke-1 size-8' />
          <span className='sr-only'>Back</span>
        </Button>
      </div>

      <h1 className='text-lg font-semibold'>{t('connect_with_device')}</h1>

      <div className='grid place-items-stretch grid-cols-[1fr_50px_1fr] items-start justify-center gap-12'>
        <div className='space-y-12'>
          <div className='space-y-8'>
            <p className='text-center'>{t('send_link_to_mobile')}</p>
            <p className='text-center'>{t('select_one_that_suits')}</p>
          </div>

          <div className='space-y-8'>
            <div className='flex items-center justify-center gap-x-8'>
              <div className='flex items-center w-40 gap-2'>
                <Icon icon='email' size={20} />
                <p>{t('email')}</p>
              </div>
              <Button
                className='px-6 h-9'
                onClick={() => onSendLink(ScanLinkChannel.EMAIL)}
                disabled={!!isSending}
              >
                {isSending === ScanLinkChannel.EMAIL && (
                  <Loader2
                    role='progressbar'
                    className='mr-2 animate-spin size-4'
                  />
                )}
                {t('send_link')}
              </Button>
            </div>
            <div className='flex items-center justify-center gap-x-8'>
              <div className='flex items-center w-40 gap-2'>
                <Icon icon='whatsapp' size={20} />
                <p>{t('whatsapp')}</p>
              </div>
              <Button
                className='px-6 h-9'
                onClick={() => onSendLink(ScanLinkChannel.WHATSAPP)}
                disabled={!!isSending}
              >
                {isSending === ScanLinkChannel.WHATSAPP && (
                  <Loader2
                    role='progressbar'
                    className='mr-2 animate-spin size-4'
                  />
                )}
                {t('send_link')}
              </Button>
            </div>
            <div className='flex items-center justify-center gap-x-8'>
              <div className='flex items-center w-40 gap-2'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth='1.5'
                  stroke='currentColor'
                  className='size-5'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3'
                  />
                </svg>

                <p>{t('notification')}</p>
              </div>
              <div className='relative'>
                <Button
                  className='px-6 h-9'
                  onClick={() => onSendLink(ScanLinkChannel.NOTIFICATION)}
                  disabled={!!isSending || !qrData?.device_token_flag}
                >
                  {isSending === ScanLinkChannel.NOTIFICATION && (
                    <Loader2
                      role='progressbar'
                      className='mr-2 animate-spin size-4'
                    />
                  )}
                  {t('send_link')}
                </Button>
                {!qrData.device_token_flag && (
                  <div className='absolute top-0 -right-6'>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className='text-yellow-500 size-4' />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('install_app_for_notification')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className='items-center justify-center hidden h-full lg:flex'>
          <p className='hidden text-lg font-semibold lg:block'>{t('or')}</p>
        </div>

        <div className='flex flex-col items-center justify-center space-y-8'>
          <p className='text-center'>{t('scan_qr_code_message')}</p>
          <div className='relative size-60 aspect-square'>
            <QRCode
              size={256}
              style={{
                height: 'auto',
                maxWidth: '100%',
                width: '100%',
              }}
              value={qrData.url}
              viewBox={`0 0 256 256`}
            />

            {minutes <= 0 && seconds <= 0 ? (
              <div className='absolute top-0 right-0 z-50 grid w-full h-full border backdrop-blur-sm place-content-center'>
                <Button className='rounded-md' onClick={onRegenerate}>
                  {t('generate_qr')}
                </Button>
              </div>
            ) : null}
          </div>

          {minutes <= 0 && seconds <= 0 ? (
            <p className='font-semibold text-primary'>
              {t('previous_qr_expired_msg')}
            </p>
          ) : (
            <p
              className={cn(
                'font-medium',
                (minutes < 5 && minutes > 2) || (minutes === 2 && seconds > 0)
                  ? 'text-yellow-700'
                  : minutes < 2 || (minutes === 2 && seconds < 0)
                  ? 'text-rose-500'
                  : 'text-primary'
              )}
            >
              {t('qr_expires_in')}{' '}
              <span className='font-semibold'>
                {minutes >= 2 && seconds > 0 ? minutes + 1 : minutes} mins{' '}
                {minutes < 2 && `${seconds} secs`}
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
